import { c as _c } from "react/compiler-runtime";
import { add } from 'date-fns';
import { useEffect, useRef } from 'react';
import { EventListFiltersProps } from '../../../../../typings/EventList.interface';
import { RequestListFiltersProps } from '../../../../../typings/RequestList.interface';
import { Resolution } from '../../../../../utils/chart';
import { MetricModel } from '../../../../../utils/formatMetricsQuery';
import formatMetricsQuery from '../../../../../utils/formatMetricsQuery';
import { rejection_cause_labels } from '../../../../../utils/rejection-causes';
import { StyledCard } from '../../../../common/base/Card';
import Text from '../../../../common/base/Text';
import HistogramBarChart, { HistogramBarData } from '../../../../common/Histogram/HistogramBarChart';
import useMetricsQuery from '../../../../hooks/useMetricsQuery';
const model_dimention_configs = {
  Requests: {
    rejection_cause: [{
      value: null as string | null,
      color: 'green_histogram',
      label: 'Accepted'
    }]
  },
  Events: {
    status: [{
      value: 'SUCCESSFUL',
      color: 'green_histogram',
      label: 'Succeeded'
    }, {
      value: 'FAILED',
      color: 'red_histogram',
      label: 'Failed'
    }, {
      value: 'QUEUED',
      color: 'primary',
      label: 'Pending'
    }, {
      value: 'HOLD',
      color: 'warning',
      label: 'Paused'
    }, {
      value: 'SCHEDULED',
      color: 'warning',
      label: 'Delayed'
    }, {
      value: 'CANCELLED',
      color: 'neutral_histogram',
      label: 'Cancelled'
    }]
  },
  EventRequestsTransformations: {
    log_level: [{
      value: null,
      color: 'green_histogram',
      label: 'OK'
    }, {
      value: 'info',
      color: 'green_histogram',
      label: 'Info'
    }, {
      value: 'debug',
      color: 'green_histogram',
      label: 'Debug'
    }, {
      value: 'warn',
      color: 'warning',
      label: 'Warn'
    }, {
      value: 'error',
      color: 'red_histogram',
      label: 'Error'
    }, {
      value: 'fatal',
      color: 'red_histogram',
      label: 'Fatal'
    }]
  },
  IgnoredEvents: {
    cause: [{
      value: 'TRANSFORMATION_FAILED',
      color: 'red_histogram',
      label: 'Failed'
    }]
  }
};
for (const [key, label] of Object.entries(rejection_cause_labels)) {
  model_dimention_configs.Requests.rejection_cause.push({
    value: key,
    color: 'red_histogram',
    label
  });
}
const HistogramChart: React.FC<{
  model: MetricModel;
  dimention: string;
  filters: EventListFiltersProps | RequestListFiltersProps;
  start_date: Date;
  end_date: Date;
  resolution: Resolution;
  refresh_key?: string;
}> = t0 => {
  const $ = _c(27);
  const {
    model,
    dimention,
    filters,
    start_date,
    end_date,
    resolution,
    refresh_key
  } = t0;
  const first_render_refresh_key = useRef(refresh_key);
  let t1;
  bb0: {
    if (filters.request && Object.values(filters.request).some(_temp)) {
      t1 = null;
      break bb0;
    }
    if (filters.search_term) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== dimention || $[1] !== end_date || $[2] !== filters || $[3] !== model || $[4] !== resolution.granularity || $[5] !== start_date) {
      const query_params = {
        filters: {
          ...filters,
          date: {
            min: start_date.toISOString(),
            max: end_date.toISOString()
          }
        },
        granularity: resolution.granularity,
        dimensions: [dimention]
      };
      t2 = formatMetricsQuery(model, query_params);
      $[0] = dimention;
      $[1] = end_date;
      $[2] = filters;
      $[3] = model;
      $[4] = resolution.granularity;
      $[5] = start_date;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = t2;
  }
  const metrics_query = t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      resetResultSetOnChange: false
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    raw_data,
    refetch,
    is_loading
  } = useMetricsQuery(metrics_query, t2);
  let t3;
  if ($[8] !== is_loading || $[9] !== refetch || $[10] !== refresh_key) {
    t3 = () => {
      if (refresh_key && refetch && !is_loading && first_render_refresh_key.current !== refresh_key) {
        refetch();
      }
    };
    $[8] = is_loading;
    $[9] = refetch;
    $[10] = refresh_key;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== refresh_key) {
    t4 = [refresh_key];
    $[12] = refresh_key;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== dimention || $[15] !== raw_data || $[16] !== resolution || $[17] !== start_date) {
    bb1: {
      const series = raw_data?.map(row => {
        const model_0 = Object.keys(row)[0].split(".")[0];
        return {
          date: row[`${model_0}.createdAt`],
          dimention: row[`${model_0}.${dimention}`],
          count: parseInt(row[`${model_0}.count`])
        };
      });
      if (!series) {
        t5 = null;
        break bb1;
      }
      t5 = Array.from(Array(resolution.steps).keys()).map(index => {
        const step_start = add(start_date, {
          seconds: index * resolution.unit * resolution.factor_to_seconds
        });
        const step_end = add(step_start, {
          seconds: resolution.unit * resolution.factor_to_seconds
        });
        const totals_0 = series.reduce((totals, row_0) => {
          if (new Date(row_0.date).getTime() < step_end.getTime() && new Date(row_0.date).getTime() >= step_start.getTime()) {
            const value = row_0.count;
            const dimention_0 = row_0.dimention;
            return {
              ...totals,
              total: totals.total + value,
              [dimention_0]: (totals[dimention_0] || 0) + value
            };
          }
          return totals;
        }, {
          total: 0
        });
        return {
          start: step_start,
          end: step_end,
          values: totals_0,
          total: totals_0.total
        };
      });
    }
    $[14] = dimention;
    $[15] = raw_data;
    $[16] = resolution;
    $[17] = start_date;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const bars = t5;
  const has_query = metrics_query !== null;
  if (!has_query) {
    let t6;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <StyledCard flex={{
        align: "center",
        justify: "center"
      }}><Text muted={true}>Histogram is unavailable when searching or filtering on request values.</Text></StyledCard>;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== resolution) {
      t7 = <HistogramBarChart bars={null} resolution={resolution} empty_state={t6} />;
      $[20] = resolution;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  const t6 = model_dimention_configs[model][dimention];
  let t7;
  if ($[22] !== bars || $[23] !== model || $[24] !== resolution || $[25] !== t6) {
    t7 = <HistogramBarChart bars={bars} resolution={resolution} dimension_configs={t6} tooltip_label={model} />;
    $[22] = bars;
    $[23] = model;
    $[24] = resolution;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
};
export default HistogramChart;
function _temp(v) {
  return v !== undefined;
}